import { format, startOfMonth } from './mes';

// ============================================================================
// BALANÇA DE CONVERGÊNCIA — versão EM LOTE (Douglas 29/07/2026)
//
// `useComissaoPerdidaConvergencia` resolve UMA pessoa e, pra isso, faz ~8 idas
// ao banco (RPC de hierarquia, config, 3 views de convergência, usuários,
// gestores, pedidos). Serve pro card "Por Pessoa", onde é uma pessoa só.
//
// A aba "Por Equipe" e o Excel precisam do saldo de TODO MUNDO. Chamar aquele
// hook por pessoa seria ~8 × N requisições — e a view mais pesada
// (`v_convergencia_oportunidade`) sozinha leva ~700ms. Era isso que fazia a
// balança demorar; e como o painel não chamava nada, o valor dele simplesmente
// não tinha a balança e não batia com o card.
//
// Aqui é 3 consultas para o mês INTEIRO, agregadas por consultor em memória:
//   1. convergencia_comissao_config  → % por cargo (carry-forward por competência)
//   2. v_convergencia_oportunidade   → faturamento PENALIZÁVEL por consultor
//   3. v_convergencia_bonus          → faturamento BONIFICÁVEL por consultor
//
// A MATEMÁTICA É A MESMA do hook individual — perda = fat × pct, ganho = fat ×
// pct, mesmo % dos dois lados. Só a forma de buscar muda. Os detalhes por
// pedido/cliente (listas para o modal) continuam só no hook individual: o
// painel precisa do número, não da lista.
// ============================================================================

/** Vigência da regra de convergência — igual ao hook individual. */
const CONVERGENCIA_VIGENCIA_INICIO = '2026-06';

/** Linha de `convergencia_comissao_config` (tabela fora dos tipos gerados). */
interface CfgRow { cargo: string | null; penalidade_pct: number | null; ativo: boolean | null; competencia: string }
/** Linha das views de convergência — só o que este hook usa. */
interface SubtotalRow { consultor_id: string | null; subtotal: number | null }

export interface ConvergenciaSaldoLote {
  /** fatPenalizavel por consultor_id */
  penalizavelPorConsultor: Map<string, number>;
  /** fatBonificavel por consultor_id */
  bonificavelPorConsultor: Map<string, number>;
  /** % por CARGO já resolvido por carry-forward para a competência pedida */
  pctPorCargo: Map<string, number>;
  /**
   * Saldo de uma pessoa a partir dos ids que compõem o escopo dela
   * (consultor = [ele]; gestor = hierarquia inteira) e do cargo dela.
   *
   * `bateuMeta` espelha a regra do card: o GANHO só vale se bateu meta; a PERDA
   * sempre desconta. Igual a `useVisaoComissaoSimulada`.
   */
  saldoDe: (
    consultorIds: string[],
    cargo: string,
    bateuMeta: boolean,
  ) => { perda: number; ganho: number; saldo: number; pct: number };
}

const VAZIO: ConvergenciaSaldoLote = {
  penalizavelPorConsultor: new Map(),
  bonificavelPorConsultor: new Map(),
  pctPorCargo: new Map(),
  saldoDe: () => ({ perda: 0, ganho: 0, saldo: 0, pct: 0 }),
};

/**
 * Busca crua — sem React. Existe separada do hook porque o painel "Por Equipe"
 * e o Excel já rodam dentro do próprio `queryFn`/handler deles e precisam
 * disparar isto no MESMO `Promise.all` das outras consultas (senão vira mais
 * uma ida em série, que é justamente o que estava deixando lento).
 */
export async function fetchConvergenciaSaldoLote(competencia: Date, supabase: any): Promise<ConvergenciaSaldoLote> {
  const mes = format(startOfMonth(competencia), 'yyyy-MM');
  const mesDate = `${mes}-01`;

  // Antes da vigência (jun/2026) não há desconto nem bônus retroativo.
  if (mes < CONVERGENCIA_VIGENCIA_INICIO) return VAZIO;

  const [cfgRes, penalRes, bonusRes] = await Promise.all([
    supabase.from('convergencia_comissao_config' as never)
      .select('cargo, penalidade_pct, ativo, competencia')
      .lte('competencia', mesDate),
    supabase.from('v_convergencia_oportunidade' as never)
      .select('consultor_id, subtotal')
      .eq('mes_trabalho', mes)
      .eq('penalizavel', true),
    supabase.from('v_convergencia_bonus' as never)
      .select('consultor_id, subtotal')
      .eq('mes_trabalho', mes)
      .eq('bonificavel', true),
  ]);

  // % por cargo com CARRY-FORWARD: a config mais recente com competencia <=
  // o mês visto. Mesma regra do hook individual — mês passado não pega regra
  // nova, mês futuro herda a última até alguém mudar.
  const pctPorCargo = new Map<string, number>();
  const maisRecentePorCargo = new Map<string, CfgRow>();
  for (const row of ((cfgRes.data as CfgRow[] | null) ?? [])) {
    const cargo = String(row.cargo || '').toUpperCase();
    const atual = maisRecentePorCargo.get(cargo);
    if (!atual || String(row.competencia) > String(atual.competencia)) {
      maisRecentePorCargo.set(cargo, row);
    }
  }
  for (const [cargo, row] of maisRecentePorCargo) {
    pctPorCargo.set(cargo, row.ativo !== false ? Number(row.penalidade_pct ?? 0) : 0);
  }

  const somaPorConsultor = (rows: SubtotalRow[] | null) => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) {
      if (!r.consultor_id) continue;
      m.set(r.consultor_id, (m.get(r.consultor_id) ?? 0) + Number(r.subtotal || 0));
    }
    return m;
  };

  const penalizavelPorConsultor = somaPorConsultor(penalRes.data as SubtotalRow[] | null);
  const bonificavelPorConsultor = somaPorConsultor(bonusRes.data as SubtotalRow[] | null);

  const saldoDe = (consultorIds: string[], cargo: string, bateuMeta: boolean) => {
    const pct = pctPorCargo.get(String(cargo || 'CONSULTOR').toUpperCase()) ?? 0;
    if (pct === 0 || consultorIds.length === 0) return { perda: 0, ganho: 0, saldo: 0, pct };
    // Set evita contar duas vezes se a hierarquia repetir alguém.
    const ids = new Set(consultorIds);
    let fatPenalizavel = 0;
    let fatBonificavel = 0;
    for (const id of ids) {
      fatPenalizavel += penalizavelPorConsultor.get(id) ?? 0;
      fatBonificavel += bonificavelPorConsultor.get(id) ?? 0;
    }
    const perda = fatPenalizavel * (pct / 100);
    // O ganho só vale se bateu meta — igual à comissão base.
    const ganho = bateuMeta ? fatBonificavel * (pct / 100) : 0;
    return { perda, ganho, saldo: ganho - perda, pct };
  };

  return { penalizavelPorConsultor, bonificavelPorConsultor, pctPorCargo, saldoDe };
}
